import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AppLayout from './layouts/AppLayout';
import AdminLayout from './layouts/AdminLayout';
import AgentPlatformLayout from './layouts/AgentPlatformLayout';
import ChatbotLayout from './layouts/ChatbotLayout';
import AtendimentoLayout from './layouts/AtendimentoLayout';
import { LoginPage } from './pages/Login/LoginPage';
import { SupportContactPage } from './pages/Support/SupportContactPage';
import { SystemSelectionPage } from './pages/Login/SystemSelectionPage';
import { ForgotPasswordPage } from './pages/ForgotPassword/ForgotPasswordPage';
import PublicDocumentViewPage from './pages/Documents/PublicDocumentViewPage';
import PublicFormPage from './pages/Forms/PublicFormPage';
import PublicCsatPage from './pages/Atendimento/PublicCsatPage';
import FormPreviewPage from './pages/Forms/FormPreviewPage';
import { SignatureInvitePage } from './pages/Signature/SignatureInvitePage';
import { ValidatePage } from './pages/PublicValidation/ValidatePage';
import { PrivateRoute } from './components/PrivateRoute';
import AccessDeniedPage from './components/Admin/AccessDeniedPage';
import { ToastContainer } from './components/UI/ToastProvider';
import { useMenuPermissions } from './hooks/useMenuPermissions';
import {
  resolveLoginRouteForTenant,
  tenantHasAnyLicensedModule,
  isTenantSupportExemptPath,
} from './utils/loginRedirectUtils';
import { initializeCacheCleanup } from './utils/cacheCleanup';
import { useSignalR } from './hooks/useSignalR';
import { useToast } from './components/UI/ToastProvider';
import { useInboxNotificationToasts } from './hooks/useInboxNotificationToasts';
import { isInboxStyleNotification } from './utils/notificationToastPolicy';
import {
  dispatchImportCompletedEvent,
  shouldRefreshAfterImportNotification,
} from './utils/importNotificationEvents';
import { useHelpdeskHomePath } from './hooks/useHelpdeskHomePath';
import { SUPPORT_NOTIFICATION_EVENT } from './hooks/useSupportNotifications';
import type { SupportNotification } from './services/supportNotificationService';

function SignalRNotificationListener() {
  const { addToast } = useToast();
  const { queueInboxToast } = useInboxNotificationToasts();

  useEffect(() => {
    const handleSupportNotification = (event: Event) => {
      const detail = (event as CustomEvent<SupportNotification>).detail;
      if (!detail)
      {
        return;
      }

      const title = (detail.title || '').trim();
      const message = (detail.message || '').trim();
      const text =
        title && message
          ? `${title} — ${message}`
          : title || message || 'Nova notificação de atendimento';

      const priority = String(detail.priority || '').toLowerCase();
      const toastType =
        priority === 'urgent' || priority === 'high' ? 'warning' : 'info';

      addToast(text, toastType, 5000, detail.actionUrl?.trim() || undefined);
    };

    window.addEventListener(SUPPORT_NOTIFICATION_EVENT, handleSupportNotification);
    return () => {
      window.removeEventListener(SUPPORT_NOTIFICATION_EVENT, handleSupportNotification);
    };
  }, [addToast]);

  useSignalR(
    (notification) => {
      const notificationTitle = (notification.title || '').toLowerCase();
      const chatbotAction =
        notificationTitle.includes('adicionar documento ao chatbot')
          ? 'add'
          : notificationTitle.includes('remover documento do chatbot')
            ? 'remove'
            : null;

      if (chatbotAction && notification.data?.documentId) {
        window.dispatchEvent(
          new CustomEvent('chatbot-indexing-update', {
            detail: {
              action: chatbotAction,
              success: notification.type === 'success',
              documentId: String(notification.data.documentId),
              title: notification.title,
              message: notification.message,
            },
          })
        );
      }

      const signedPdfNotificationTypes = [
        'SignedDocumentPdfProcessing',
        'SignedDocumentPdfReady',
        'SignedDocumentPdfFailed',
      ];
      if (signedPdfNotificationTypes.includes(notification.type)) {
        const documentId = notification.data?.documentId;
        if (documentId) {
          window.dispatchEvent(
            new CustomEvent('signed-final-pdf-notification', {
              detail: { documentId: String(documentId), notificationType: notification.type },
            })
          );
        }
      }

      if (isInboxStyleNotification(notification)) {
        queueInboxToast(notification);
        return;
      }

      if (shouldRefreshAfterImportNotification(notification))
      {
        dispatchImportCompletedEvent(notification);
      }

      if (notification.type === 'UserPrivateDocumentIndexing') {
        window.dispatchEvent(
          new CustomEvent('private-doc-indexing', { detail: notification.data })
        );
        const raw = notification.data?.processingStatus;
        const status = raw != null ? String(raw).toLowerCase() : '';
        if (status === 'completed') {
          addToast('Documento indexado com sucesso e disponível no chat.', 'success');
        } else if (status === 'failed' || status === 'error') {
          addToast('Falha ao indexar o documento para o chat.', 'error');
        }
        return;
      }
      const actionUrl = notification.actionUrl?.trim() || undefined;

      if (notification.type === 'success') {
        addToast(notification.message, 'success', 3000, actionUrl);
      } else if (notification.type === 'error') {
        addToast(notification.message, 'error', 3000, actionUrl);
      } else {
        addToast(notification.message, 'info', 3000, actionUrl);
      }
    },
    (job) => {
      if (job.success) {
        addToast(job.message, 'success', 5000);
      } else {
        addToast(job.message, 'warning', 8000);
      }
    }
  );

  return null;
}

function FeatureUnavailableListener() {
  const { addToast } = useToast();

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ code?: string; message?: string }>).detail;
      const message = detail?.message?.trim() || 'Funcionalidade indisponível no seu plano.';
      addToast(message, 'warning', 6000);
    };

    window.addEventListener('feature-unavailable', handler);
    return () => {
      window.removeEventListener('feature-unavailable', handler);
    };
  }, [addToast]);

  return null;
}

function App() {
  // Inicializar limpeza de cache
  useEffect(() => {
    initializeCacheCleanup();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <SignalRNotificationListener />
        <FeatureUnavailableListener />
        <TenantSupportRedirect />
        <Routes>
          {/* Rotas públicas */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/support" element={<SupportContactPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/documents/public/:documentId" element={<PublicDocumentViewPage />} />
          <Route path="/forms/public/:publicId" element={<PublicFormPage />} />
          <Route path="/csat/:uuid" element={<PublicCsatPage />} />
          <Route
            path="/forms/:formId/preview"
            element={
              <PrivateRoute requiredRoles={['admin', 'manager', 'editor']}>
                <FormPreviewPage />
              </PrivateRoute>
            }
          />
          <Route path="/validate/:publicId" element={<ValidatePage />} />
          <Route path="/validate" element={<ValidatePage />} />
          <Route
            path="/sign/inbox"
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          />
          <Route
            path="/sign/inbox/:documentId"
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          />
          <Route path="/sign/:token" element={<SignatureInvitePage />} />
          <Route
            path="/unauthorized"
            element={
              <PrivateRoute>
                <AccessDeniedPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/system-selection"
            element={
              <PrivateRoute>
                <SystemSelectionPage />
              </PrivateRoute>
            }
          />

          {/* Rotas autenticadas */}
          <Route
            path="/admin/*"
            element={
              <PrivateRoute requiredRoles={['admin']}>
                <AdminLayout />
              </PrivateRoute>
            }
          />
          <Route
            path="/agent-platform/*"
            element={
              <PrivateRoute requiredRoles={['admin']}>
                <AgentPlatformLayout />
              </PrivateRoute>
            }
          />
          <Route
            path="/chatbot-admin/*"
            element={
              <PrivateRoute requiredRoles={['admin']}>
                <ChatbotLayout />
              </PrivateRoute>
            }
          />
          <Route
            path="/system-selection"
            element={
              <PrivateRoute>
                <SystemSelectionPage />
              </PrivateRoute>
            }
          />
          <Route path="/atendimento/*" element={<AtendimentoLegacyRedirect />} />
          <Route
            path="/helpdesk/*"
            element={
              <PrivateRoute requiredRoles={['admin', 'manager']}>
                <AtendimentoLayout />
              </PrivateRoute>
            }
          />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<RootRedirect />} />
        </Routes>
        <ToastContainer />
      </AuthProvider>
    </Router>
  );
}

const TenantSupportRedirect: React.FC = () => {
  const { tenantBootstrap, publicTenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (tenantBootstrap.kind !== 'success') {
      return;
    }

    if (tenantHasAnyLicensedModule(publicTenant?.moduleCodes)) {
      return;
    }

    if (isTenantSupportExemptPath(location.pathname)) {
      return;
    }

    navigate('/support', { replace: true });
  }, [tenantBootstrap.kind, publicTenant, location.pathname, navigate]);

  return null;
};

const AtendimentoLegacyRedirect: React.FC = () => {
  const { homePath } = useHelpdeskHomePath();
  return <Navigate to={homePath} replace />;
};

const RootRedirect: React.FC = () => {
  const { user, tenantBootstrap, publicTenant } = useAuth();
  const { getHomePage } = useMenuPermissions();

  if (tenantBootstrap.kind === 'success' && !tenantHasAnyLicensedModule(publicTenant?.moduleCodes)) {
    return <Navigate to="/support" replace />;
  }

  if (user) {
    return <Navigate to={getHomePage()} replace />;
  }

  if (tenantBootstrap.kind === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen" aria-live="polite">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  return (
    <Navigate
      to={resolveLoginRouteForTenant(publicTenant?.moduleCodes, publicTenant?.defaultProductModuleCode)}
      replace
    />
  );
};

export default App;
